/**
 * <ToastProvider> + <ToastContainer> — UI du store toast.
 * Pas de portal : `position: fixed` suffit pour notre besoin.
 *
 * Branchement : <ToastProvider> wrap l'app, monte automatiquement le container.
 */

import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { toast as toastApi, useToasts, type ToastKind } from "../lib/toast";
import { cn } from "../lib/cn";

const KIND_STYLES: Record<ToastKind, string> = {
  success: "border-l-green-500",
  error: "border-l-red-500",
  info: "border-l-brand-500",
};

const KIND_ICON: Record<ToastKind, string> = {
  success: "✓",
  error: "✕",
  info: "i",
};

const KIND_ICON_BG: Record<ToastKind, string> = {
  success: "bg-green-100 text-green-700",
  error: "bg-red-100 text-red-700",
  info: "bg-brand-100 text-brand-700",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <ToastContainer />
    </>
  );
}

function ToastContainer() {
  const { t } = useTranslation();
  const items = useToasts();

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex max-w-[calc(100%-2rem)] flex-col gap-2"
    >
      {items.map((item) => (
        <div
          key={item.id}
          role={item.kind === "error" ? "alert" : "status"}
          className={cn(
            "pointer-events-auto flex w-80 max-w-full items-start gap-3 rounded-lg border border-[color:var(--brand-border)] border-l-4 bg-[color:var(--brand-surface)] p-3 shadow-lg",
            "animate-[toast-in_180ms_ease-out]",
            KIND_STYLES[item.kind],
          )}
        >
          <span
            aria-hidden="true"
            className={cn(
              "mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold",
              KIND_ICON_BG[item.kind],
            )}
          >
            {KIND_ICON[item.kind]}
          </span>
          <p className="flex-1 text-sm leading-snug text-[color:var(--brand-text)]">
            {item.message}
          </p>
          <button
            type="button"
            onClick={() => toastApi.dismiss(item.id)}
            aria-label={t("common.toast.dismiss")}
            className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      ))}
    </div>
  );
}
